import React from 'react'
import {useSelector} from 'react-redux'
import {OfferPrice} from 'components/Offer/Shared/OfferPrice'
import {GHAPreferredRateTotalPrice} from 'components/Offer/Shared/OfferPrice/GHAPreferredRateTotalPrice'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getAppLockedDealConfig,
  getOfferAccessFlowSettings,
  getShowTotalPriceToggle
} from 'modules/sapiSearch/selectors'
import {getIsLockedDealExcludingGHA} from 'modules/search/selectors'
import {toggle} from 'opticks'
import {getIsAccommodationPage} from 'utils/getIsAccommodationPage'

import {OfferSecondaryLayoutVariant} from '@daedalus/core/src/offer/types/OfferLayout'
import {SearchOffer} from '@daedalus/core/src/offer/types/SearchOffer'

interface MainProps {
  hotelId: string
  anchorPriceTotal: number
  anchorPrice: number
  offer: SearchOffer
  showSignInToUnlock: boolean
  variant: OfferSecondaryLayoutVariant
  isSplitBooking?: false
  isRequiredOneTimePasswordFlow?: boolean
  showGHAPreferredRateTotalPrice?: boolean
  showPriceIndication?: boolean
}

interface SplitBookingProps {
  isSplitBooking: true
  hotelId: string
  anchorPriceTotal?: never
  anchorPrice?: never
  offer?: never
  showSignInToUnlock?: never
  variant?: never
  isRequiredOneTimePasswordFlow?: boolean
  showGHAPreferredRateTotalPrice?: never
  showPriceIndication?: never
}

export const PriceComponent: React.FC<MainProps | SplitBookingProps> = ({
  hotelId,
  anchorPriceTotal,
  anchorPrice,
  offer,
  variant,
  isSplitBooking,
  // remove this prop on B side of 7d6464d8-move-auth-to-ap
  isRequiredOneTimePasswordFlow,
  showGHAPreferredRateTotalPrice = false,
  showPriceIndication = false
}) => {
  const showTotalPriceToggle = useSelector(getShowTotalPriceToggle)
  const currencyCode = useSelector(getCurrencyCode)
  const isAccommodationPage = getIsAccommodationPage()

  const isLocked =
    useSelector(getIsLockedDealExcludingGHA(offer, hotelId)) ||
    isRequiredOneTimePasswordFlow

  // Added as part of 7d6464d8-move-auth-to-ap
  const isMoveAuthToAPEnabled = toggle('7d6464d8-move-auth-to-ap', false, true)
  const {isSpecial, isAuthOrSMSLocked, isAppLocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const {isAppLockedDeal} = useSelector(state =>
    getAppLockedDealConfig(state, hotelId, offer?.id)
  )

  if (isSplitBooking) {
    return (
      <div data-id="Price">
        <OfferPrice isSplitBooking size="sm" hotelId={hotelId} />
      </div>
    )
  }

  if (!showTotalPriceToggle) {
    return (
      <div data-id="Price">
        <OfferPrice
          showAnchorPrice={false}
          offer={offer}
          anchorPrice={anchorPrice}
          hotelId={hotelId}
          size="sm"
          showLockIcon={
            variant === 'SecondaryDefault' &&
            (isMoveAuthToAPEnabled ? isAuthOrSMSLocked : true)
          }
          showAppIcon={
            variant === 'SecondaryDefault' &&
            (isMoveAuthToAPEnabled ? isAppLocked : true)
          }
          showPriceIndication={isAccommodationPage}
          isPrivateDeal={isMoveAuthToAPEnabled ? isSpecial : isLocked}
          isLockedDeal={
            isMoveAuthToAPEnabled
              ? isAuthOrSMSLocked
              : isAppLockedDeal || isLocked
          }
          isAppLockedDeal={
            isMoveAuthToAPEnabled ? isAppLocked : isAppLockedDeal
          }
        />
      </div>
    )
  }

  return (
    <div data-id="Price">
      <OfferPrice
        showAnchorPrice={false}
        anchorPriceTotal={anchorPriceTotal}
        anchorPrice={anchorPrice}
        offer={offer}
        hotelId={hotelId}
        size="sm"
        showLockIcon={
          variant === 'SecondaryDefault' &&
          (isMoveAuthToAPEnabled ? isAuthOrSMSLocked : true)
        }
        showAppIcon={
          variant === 'SecondaryDefault' &&
          (isMoveAuthToAPEnabled ? isAppLocked : true)
        }
        showPriceIndication={showPriceIndication}
        isPrivateDeal={isMoveAuthToAPEnabled ? isSpecial : isLocked}
        isLockedDeal={
          isMoveAuthToAPEnabled
            ? isAuthOrSMSLocked
            : isAppLockedDeal || isLocked
        }
        isAppLockedDeal={isMoveAuthToAPEnabled ? isAppLocked : isAppLockedDeal}
      />
      {showGHAPreferredRateTotalPrice && (
        <GHAPreferredRateTotalPrice
          value={offer.totalRate}
          currency={currencyCode as string}
        />
      )}
    </div>
  )
}
